import { useState, useMemo, useCallback, useEffect } from "react";
import { ArrowLeft, ArrowRight, Minus, Plus, ChevronDown } from "lucide-react";
import {
  products,
  priceTiers,
  DRINKS_PER_TUBE,
  type Bundle,
  type CartItem,
  type OrderMode,
} from "@/data/pricing";
import { useCheckout } from "@/contexts/CheckoutContext";
import ProductInfoPanel from "@/components/ProductInfoPanel";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";

interface BundleCustomizerProps {
  bundle: Bundle;
  mode: OrderMode;
  onBack: () => void;
}

const initialDistribution = (totalTubes: number): Record<string, number> => {
  const base = Math.floor(totalTubes / products.length);
  const remainder = totalTubes % products.length;
  return Object.fromEntries(
    products.map((p, i) => [p.id, base + (i < remainder ? 1 : 0)])
  );
};

const BundleCustomizer = ({ bundle, mode, onBack }: BundleCustomizerProps) => {
  const { openCheckout } = useCheckout();
  const tier = priceTiers.find((t) => t.tubes === bundle.tubes);
  const price = tier
    ? mode === "purchase"
      ? tier.purchasePrice
      : tier.subscriptionPrice
    : 0;

  const [quantities, setQuantities] = useState<Record<string, number>>(() =>
    initialDistribution(bundle.tubes)
  );
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // Reset distribution when bundle changes
  useEffect(() => {
    setQuantities(initialDistribution(bundle.tubes));
    setExpandedId(null);
  }, [bundle.tubes]);

  const totalQty = useMemo(
    () => Object.values(quantities).reduce((s, q) => s + q, 0),
    [quantities]
  );

  const remaining = bundle.tubes - totalQty;
  const isComplete = totalQty === bundle.tubes;

  const updateQty = useCallback(
    (id: string, delta: number) => {
      setQuantities((prev) => {
        const next = Math.max(0, prev[id] + delta);
        const otherTotal = Object.entries(prev)
          .filter(([k]) => k !== id)
          .reduce((s, [, v]) => s + v, 0);
        if (otherTotal + next > bundle.tubes) return prev;
        return { ...prev, [id]: next };
      });
    },
    [bundle.tubes]
  );

  const handleContinue = () => {
    if (!isComplete || !tier) return;
    const cart: CartItem[] = products
      .filter((p) => quantities[p.id] > 0)
      .map((p) => ({ product: p, quantity: quantities[p.id] }));

    openCheckout(cart, mode, price);
  };

  const totalDrinks = bundle.tubes * DRINKS_PER_TUBE;

  return (
    <div>
      <button
        onClick={onBack}
        className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-muted-foreground hover:text-foreground transition-colors mb-6"
      >
        <ArrowLeft className="w-3.5 h-3.5" />
        Tillbaka till paket
      </button>

      <div className="mb-8">
        <div className="flex items-baseline gap-3 flex-wrap">
          <h2 className="text-3xl md:text-4xl font-black uppercase text-foreground tracking-tight">
            {bundle.name}
          </h2>
        </div>
        <p className="text-sm text-muted-foreground mt-2 tracking-wide">
          Passar {bundle.people} · {bundle.tubes} tuber · {totalDrinks} drycker · {price} kr
          {mode === "subscription" ? "/mån" : ""}
        </p>
        <p className="text-xs text-foreground/70 mt-3 tracking-wide">
          Anpassa fördelningen mellan smaker. Du måste välja totalt {bundle.tubes} tuber.
        </p>
      </div>

      <div className="space-y-3">
        {products.map((product) => {
          const qty = quantities[product.id];
          const canAdd = totalQty < bundle.tubes;
          const isExpanded = expandedId === product.id;
          return (
            <Collapsible
              key={product.id}
              open={isExpanded}
              onOpenChange={(open) =>
                setExpandedId(open ? product.id : null)
              }
              className={`rounded-2xl bg-gradient-to-br ${product.gradient} overflow-hidden`}
            >
              <div className="w-full flex items-center gap-4 p-4">
                <CollapsibleTrigger asChild>
                  <button
                    type="button"
                    className="flex-1 min-w-0 flex items-center gap-4 text-left"
                    aria-label={`Visa info om ${product.name}`}
                  >
                    <img
                      src={product.image}
                      alt={product.name}
                      className="w-16 h-16 object-contain shrink-0 drop-shadow"
                    />
                    <div className="flex-1 min-w-0">
                      <h3 className="text-base md:text-lg font-black uppercase text-foreground tracking-tight leading-none">
                        {product.name}
                      </h3>
                      <p className="text-[11px] text-foreground/60 mt-1 tracking-wide">
                        {product.description}
                      </p>
                    </div>
                    <ChevronDown
                      className={`w-4 h-4 text-foreground/60 shrink-0 transition-transform duration-300 ${
                        isExpanded ? "rotate-180" : ""
                      }`}
                    />
                  </button>
                </CollapsibleTrigger>
                <div className="flex items-center gap-3 shrink-0">
                  <button
                    type="button"
                    onClick={() => updateQty(product.id, -1)}
                    disabled={qty === 0}
                    className="w-9 h-9 rounded-full bg-foreground/10 hover:bg-foreground/20 disabled:opacity-20 flex items-center justify-center transition-all active:scale-90"
                    aria-label={`Ta bort ${product.name}`}
                  >
                    <Minus className="w-3.5 h-3.5 text-foreground" />
                  </button>
                  <span className="text-2xl font-black text-foreground tabular-nums leading-none w-8 text-center">
                    {qty}
                  </span>
                  <button
                    type="button"
                    onClick={() => updateQty(product.id, 1)}
                    disabled={!canAdd}
                    className="w-9 h-9 rounded-full bg-foreground hover:bg-foreground/90 disabled:opacity-20 flex items-center justify-center transition-all active:scale-90"
                    aria-label={`Lägg till ${product.name}`}
                  >
                    <Plus className="w-3.5 h-3.5 text-background" />
                  </button>
                </div>
              </div>
              <CollapsibleContent className="overflow-hidden data-[state=open]:animate-collapsible-down data-[state=closed]:animate-collapsible-up">
                <div className="px-4 pb-4 pt-1 border-t border-foreground/10">
                  <ProductInfoPanel productId={product.id} variant="bubble" />
                </div>
              </CollapsibleContent>
            </Collapsible>
          );
        })}
      </div>

      {/* Sticky footer */}
      <div className="fixed bottom-0 left-0 right-0 bg-white/80 backdrop-blur-md border-t border-foreground/10 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold text-foreground uppercase tracking-wider">
              {totalQty}/{bundle.tubes} tuber valda — {price} kr
              {mode === "subscription" ? "/mån" : ""}
            </p>
            {!isComplete && (
              <p className="text-[10px] text-muted-foreground mt-0.5 tracking-wide">
                {remaining > 0
                  ? `Lägg till ${remaining} till för att slutföra ditt paket`
                  : `Ta bort ${-remaining} för att slutföra ditt paket`}
              </p>
            )}
            {isComplete && (
              <p className="text-[10px] text-muted-foreground mt-0.5 tracking-wide">
                Ditt paket är komplett — gå vidare till kassan
              </p>
            )}
          </div>

          <button
            onClick={handleContinue}
            disabled={!isComplete}
            className="shrink-0 border border-foreground text-foreground bg-transparent px-6 py-3 rounded-lg font-medium uppercase tracking-widest text-xs hover:bg-foreground hover:text-background transition-all duration-300 disabled:opacity-30 disabled:cursor-not-allowed inline-flex items-center gap-2"
          >
            Gå vidare
            <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default BundleCustomizer;
